import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '@/lib/errorMessage';

export interface ForecastDay {
  date: string;
  temperature_min: number;
  temperature_max: number;
  cloud_cover: number;
  precipitation: number;
  wind_speed_max: number;
}

export interface HistoricalMonth {
  month: number;
  monthName: string;
  temperature_avg: number;
  temperature_min: number;
  temperature_max: number;
  cloud_cover_avg: number;
  precipitation_avg: number;
  wind_speed_avg: number;
  humidity_avg: number;
  years_count: number;
}

export interface TripStopWeatherData {
  source: 'forecast' | 'historical';
  daily?: ForecastDay[];
  monthly?: HistoricalMonth[];
}

interface UseTripStopWeatherProps {
  latitude: number;
  longitude: number;
  arrivalDate: string;
  departureDate: string;
  enabled?: boolean;
}

export const useTripStopWeather = ({
  latitude,
  longitude,
  arrivalDate,
  departureDate,
  enabled = true,
}: UseTripStopWeatherProps) => {
  const [data, setData] = useState<TripStopWeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isSubscribed } = useAuth();

  const fetchWeather = useCallback(async () => {
    if (!enabled || !user || !isSubscribed || !latitude || !longitude) return;

    setLoading(true);
    setError(null);

    try {
      const response = await supabase.functions.invoke(
        'get-trip-stop-weather',
        {
          body: {
            latitude,
            longitude,
            arrivalDate,
            departureDate,
            userId: user.id,
          },
        }
      );

      if (response.error) {
        // Extract the response body for better debugging
        const errorContext = response.error as { context?: { body?: BodyInit } };
        let body = '';
        try {
          if (errorContext?.context?.body) {
            body = await new Response(errorContext.context.body).text();
          }
        } catch { /* ignore */ }
        console.error('Trip stop weather error:', response.error.message, 'body:', body);
        throw new Error(body ? JSON.parse(body)?.error || response.error.message : response.error.message);
      }

      const apiData = response.data;
      if (apiData?.error) {
        console.error('Trip stop weather API error:', apiData.error);
        throw new Error(apiData.error);
      }

      setData(apiData as TripStopWeatherData);
    } catch (err) {
      console.error('Error fetching trip stop weather:', err);
      setError(getErrorMessage(err) || 'Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude, arrivalDate, departureDate, user, isSubscribed, enabled]);

  useEffect(() => {
    if (enabled && isSubscribed) {
      fetchWeather();
    }
  }, [fetchWeather, enabled, isSubscribed]);

  const retry = useCallback(() => {
    fetchWeather();
  }, [fetchWeather]);

  return { data, loading, error, retry };
};
